import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

// ── 提前清除失效 token，避免 Supabase 自動刷新時拋錯 ──────────
function clearStaleAuthTokens() {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            const expiresAt: number | undefined = parsed?.expires_at;
            // 若 token 已過期超過 1 小時，提前清除
            if (expiresAt && Date.now() / 1000 > expiresAt + 3600) {
              localStorage.removeItem(key);
            }
          }
        } catch {
          localStorage.removeItem(key);
        }
      }
    });
  } catch {
    // ignore
  }
}
clearStaleAuthTokens();

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    headers: {
      'X-Client-Info': 'ailanshe-web',
    },
  },
});

// ── 全域捕捉 Refresh Token 失效錯誤，靜默登出 ───────────────
const REFRESH_TOKEN_ERRORS = [
  'Invalid Refresh Token',
  'Refresh Token Not Found',
  'refresh_token_not_found',
  'invalid_grant',
];

function isRefreshTokenError(msg: string): boolean {
  return REFRESH_TOKEN_ERRORS.some((e) => msg.includes(e));
}

window.addEventListener('unhandledrejection', (event) => {
  const msg =
    (event.reason instanceof Error ? event.reason.message : String(event.reason)) ?? '';
  if (isRefreshTokenError(msg)) {
    event.preventDefault(); // 不顯示在 console
    supabase.auth.signOut().catch(() => {});
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          localStorage.removeItem(key);
        }
      });
    } catch { /* ignore */ }
  }
});

// ── 監聽 auth 狀態，清除殘留 token ──────────────────────────
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          localStorage.removeItem(key);
        }
      });
    } catch { /* ignore */ }
  }
});

// ── 安全工具 ──────────────────────────────────────────────

/**
 * XSS 防護：過濾危險 HTML 字元
 */
export function sanitizeText(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
}

/**
 * 驗證 Email 格式
 */
export function isValidEmail(email: string): boolean {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(email);
}

/**
 * 驗證台灣手機號碼格式
 */
export function isValidPhone(phone: string): boolean {
  const re = /^09\d{8}$/;
  return re.test(phone.replace(/[-\s]/g, ''));
}

/**
 * 前端速率限制：防止暴力攻擊
 * key: 操作識別符（例如 "login:user@email.com"）
 * maxAttempts: 最多幾次
 * windowMs: 時間窗口（毫秒）
 */
export function checkRateLimit(
  key: string,
  maxAttempts: number = 5,
  windowMs: number = 60_000
): { allowed: boolean; remainingMs?: number } {
  const storageKey = `rl_${key}`;
  const now = Date.now();

  try {
    const raw = sessionStorage.getItem(storageKey);
    const data: { attempts: number; firstAttemptAt: number } = raw
      ? JSON.parse(raw)
      : { attempts: 0, firstAttemptAt: now };

    // 超過時間窗口就重置
    if (now - data.firstAttemptAt > windowMs) {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({ attempts: 1, firstAttemptAt: now })
      );
      return { allowed: true };
    }

    data.attempts += 1;
    sessionStorage.setItem(storageKey, JSON.stringify(data));

    if (data.attempts > maxAttempts) {
      const remainingMs = windowMs - (now - data.firstAttemptAt);
      return { allowed: false, remainingMs };
    }

    return { allowed: true };
  } catch {
    return { allowed: true };
  }
}

/**
 * 清除速率限制（登入成功後呼叫）
 */
export function clearRateLimit(key: string): void {
  try {
    sessionStorage.removeItem(`rl_${key}`);
  } catch {
    // ignore
  }
}

// ── 資料介面定義 ───────────────────────────────────────────

export interface Property {
  id: string;
  user_id: string;
  title: string;
  price: number;
  area: number;
  rooms: number;
  bathrooms: number;
  location: string;
  address: string;
  description: string | null;
  furniture: string | null;
  pet_allowed: string | null;
  water_bill: string | null;
  electric_bill: string | null;
  landlord_name: string;
  landlord_phone: string;
  landlord_email: string;
  photos: string[];
  photo_positions: string[] | null;
  photo_zooms: number[] | null;
  status: string;
  view_count: number;
  screening_request_count: number;
  property_type: string | null;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  property_id: string;
  landlord_id: string;
  sender_name: string;
  sender_phone: string | null;
  sender_email: string | null;
  message: string;
  property_title: string | null;
  is_read: boolean;
  created_at: string;
}

export interface TenantMatchRequest {
  id: string;
  user_id: string | null;
  session_token: string | null;
  region: string;
  max_price: number;
  min_area: number | null;
  rooms: string[];
  pet_allowed: boolean;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  notified: boolean;
  created_at: string;
}

// ── 租客媒合功能 ───────────────────────────────────────────

export const createTenantMatchRequest = async (data: {
  region: string;
  max_price: number;
  min_area?: number;
  rooms: string[];
  pet_allowed: boolean;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  line_id?: string;
  user_id?: string;
  session_token?: string;
}) => {
  const { data: result, error } = await supabase
    .from('tenant_match_requests')
    .insert([data])
    .select()
    .single();

  if (error) throw error;
  return result;
};

export const getMatchingTenantRequests = async (property: {
  location: string;
  price: number;
  area: number;
  rooms: number;
  pet_allowed: string | null;
}) => {
  let query = supabase
    .from('tenant_match_requests')
    .select('*')
    .eq('notified', false)
    .gte('max_price', property.price);

  if (property.location !== '全部') {
    query = query.or(`region.eq.全部,region.eq.${property.location}`);
  }

  if (property.area) {
    query = query.or(`min_area.is.null,min_area.lte.${property.area}`);
  }

  const { data, error } = await query;

  if (error) throw error;

  return data?.filter((request) => {
    const roomMatch =
      request.rooms.includes('全部') ||
      request.rooms.includes(`${property.rooms}房`) ||
      (property.rooms >= 4 && request.rooms.includes('4房以上'));

    const petMatch = !request.pet_allowed || property.pet_allowed === '可';

    return roomMatch && petMatch;
  }) || [];
};

export const updateTenantMatchNotified = async (ids: string[]) => {
  const { error } = await supabase
    .from('tenant_match_requests')
    .update({ notified: true })
    .in('id', ids);

  if (error) throw error;
};

export const getTenantMatchCount = async () => {
  const { count, error } = await supabase
    .from('tenant_match_requests')
    .select('*', { count: 'exact', head: true })
    .eq('notified', false);

  if (error) throw error;
  return count || 0;
};
